import React, { useState, useMemo } from 'react';
import { Home, Hammer, AlertTriangle, CheckCircle2, TrendingDown } from 'lucide-react';
import { Card, Input } from '../ui';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { cn } from '../../lib/utils';

interface Repair {
  id: string;
  name: string;
  cost: number;
  yearNeeded: number;
}

const DEFAULT_REPAIRS: Repair[] = [
  { id: '1', name: 'Roof Replacement (Bldg A)', cost: 120000, yearNeeded: 3 },
  { id: '2', name: 'Asphalt Resurfacing', cost: 45000, yearNeeded: 5 },
  { id: '3', name: 'Pool Pump & Deck', cost: 25000, yearNeeded: 7 }
];

export function HoaReserveFundCalculator() {
  const [currentReserve, setCurrentReserve] = useState<number>(85000);
  const [monthlyContribution, setMonthlyContribution] = useState<number>(3500);
  const [repairs, setRepairs] = useState<Repair[]>(DEFAULT_REPAIRS);

  const model = useMemo(() => {
    const data = [];
    let balance = currentReserve;
    let depletedYear = null;
    let lowestBalance = currentReserve;

    const annualContribution = monthlyContribution * 12;

    for (let yr = 1; yr <= 10; yr++) {
      balance += annualContribution;
      
      const repairsThisYear = repairs.filter(r => r.yearNeeded === yr);
      const repairCostThisYear = repairsThisYear.reduce((sum, r) => sum + r.cost, 0);
      
      balance -= repairCostThisYear;

      if (balance < lowestBalance) {
        lowestBalance = balance;
      }

      if (balance < 0 && !depletedYear) {
        depletedYear = yr;
      }

      data.push({
        year: `Year ${yr}`,
        balance: Math.max(0, balance),
        deficit: balance < 0 ? Math.abs(balance) : 0,
        repairCost: repairCostThisYear
      });
    }

    const totalRepairLiability = repairs.reduce((sum, r) => sum + r.cost, 0);
    // Rough "Percent Funded" calculation based on current cash vs future 10-year liability
    // (A true reserve study is much more complex, but this serves as a good benchmark)
    const percentFunded = totalRepairLiability > 0 ? (currentReserve / totalRepairLiability) * 100 : 100;

    return {
      data,
      depletedYear,
      lowestBalance,
      percentFunded,
      totalRepairLiability
    };
  }, [currentReserve, monthlyContribution, repairs]);

  const formatCurrency = (val: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val);

  return (
    <div className="max-w-6xl mx-auto space-y-8 p-4">
      <div className="bg-gradient-to-r from-teal-700 to-emerald-900 rounded-2xl p-8 text-white shadow-xl flex flex-col md:flex-row items-center justify-between gap-6">
        <div>
          <h2 className="text-2xl font-black flex items-center mb-2">
            <Home className="w-6 h-6 mr-3 text-teal-300" />
            HOA Reserve Fund Calculator
          </h2>
          <p className="text-teal-100 max-w-xl">
            Project your HOA's reserve solvency over the next 10 years. Model your monthly contributions against upcoming major capital expenditures to avoid emergency special assessments.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-8">
        
        {/* Controls */}
        <div className="md:col-span-1 space-y-6">
          <Card className="p-6 space-y-6 border-slate-200 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900">
            <div>
              <label className="text-sm font-semibold text-slate-500 uppercase block mb-2">Current Reserve Balance</label>
              <Input 
                type="number" 
                value={currentReserve} 
                onChange={(e) => setCurrentReserve(Number(e.target.value))}
                className="text-lg font-bold"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-500 uppercase block mb-2">Monthly Contribution</label>
              <Input 
                type="number" 
                value={monthlyContribution} 
                onChange={(e) => setMonthlyContribution(Number(e.target.value))}
              />
              <p className="text-xs text-slate-400 mt-1">Amount allocated to reserves from monthly dues.</p>
            </div>
          </Card>

          <Card className="p-6 space-y-4 border-slate-200 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900">
            <h3 className="font-bold border-b border-slate-100 dark:border-slate-800 pb-2 flex items-center">
              <Hammer className="w-4 h-4 mr-2 text-slate-400" /> Capital Expenditures
            </h3>
            
            <div className="space-y-4">
              {repairs.map((repair, i) => (
                <div key={repair.id} className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-100 dark:border-slate-700">
                  <div className="font-semibold text-sm mb-2">{repair.name}</div>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <label className="text-xs text-slate-500 uppercase">Cost ($)</label>
                      <Input 
                        type="number" 
                        value={repair.cost} 
                        onChange={(e) => {
                          const newRepairs = [...repairs];
                          newRepairs[i].cost = Number(e.target.value);
                          setRepairs(newRepairs);
                        }}
                        className="h-8 text-sm"
                      />
                    </div>
                    <div className="w-20">
                      <label className="text-xs text-slate-500 uppercase">Year</label>
                      <Input 
                        type="number" 
                        min="1" max="10"
                        value={repair.yearNeeded} 
                        onChange={(e) => {
                          const newRepairs = [...repairs];
                          newRepairs[i].yearNeeded = Number(e.target.value);
                          setRepairs(newRepairs);
                        }}
                        className="h-8 text-sm"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Visuals */}
        <div className="md:col-span-2 space-y-6">
          <div className="grid sm:grid-cols-2 gap-6">
            <Card className={cn("p-6 border-slate-200 dark:border-slate-800 shadow-xl text-white", model.depletedYear ? "bg-red-900" : "bg-emerald-800")}>
              <div className="flex items-start justify-between mb-2">
                <h3 className="text-sm font-bold text-white/70 uppercase tracking-wider">10-Year Solvency</h3>
                {model.depletedYear ? <AlertTriangle className="w-8 h-8 text-red-300" /> : <CheckCircle2 className="w-8 h-8 text-emerald-300" />}
              </div>
              
              <div className="text-3xl font-black tracking-tight mb-2">
                {model.depletedYear ? `Depleted in Year ${model.depletedYear}` : 'Fully Solvent'}
              </div>
              <p className="text-white/80 text-sm">
                {model.depletedYear 
                  ? "You will need to levy a special assessment to cover planned repairs." 
                  : `Your lowest projected balance is ${formatCurrency(model.lowestBalance)}.`}
              </p>
            </Card>

            <Card className="p-6 border-slate-200 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900">
              <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-2">Funded Status Benchmark</h3>
              <div className="text-4xl font-black text-slate-900 dark:text-white mb-2">
                {model.percentFunded.toFixed(1)}%
              </div>
              
              <div className="h-2 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                <div 
                  className={cn("h-full transition-all", model.percentFunded < 30 ? "bg-red-500" : model.percentFunded < 70 ? "bg-yellow-500" : "bg-emerald-500")}
                  style={{ width: `${Math.min(100, model.percentFunded)}%` }}
                />
              </div>
              <p className="text-xs text-slate-500 mt-2">Current cash vs 10-year repair liability. (&gt;70% is healthy)</p>
            </Card>
          </div>

          <Card className="p-6 border-slate-200 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900 h-[400px]">
            <h3 className="text-lg font-bold mb-6 flex items-center">
              <TrendingDown className="w-5 h-5 mr-2 text-teal-500" /> Projected Reserve Balance
            </h3>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={model.data} margin={{ top: 5, right: 20, bottom: 25, left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#334155" opacity={0.2} />
                <XAxis 
                  dataKey="year" 
                  tick={{ fill: '#64748b', fontSize: 12 }}
                  tickMargin={10}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis 
                  tickFormatter={(val) => `$${val/1000}k`}
                  tick={{ fill: '#64748b', fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip 
                  formatter={(value: any, name: any) => [formatCurrency(Number(value)), String(name) === 'balance' ? 'Reserve Balance' : 'Repair Cost']}
                  contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                />
                <ReferenceLine y={0} stroke="#ef4444" strokeWidth={2} />
                <Line 
                  type="monotone" 
                  dataKey="balance" 
                  name="Reserve Balance"
                  stroke={model.depletedYear ? '#ef4444' : '#0f766e'} 
                  strokeWidth={3} 
                  dot={{ r: 4, strokeWidth: 2 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </div>

      </div>
    </div>
  );
}
